import API from '../config/apis';
import Store from '../redux/store';
import { logout } from '../redux/slice/userSlice';
import { getAccessToken, setAccessToken, decodeTokenExp, subscribeAccessToken, getRefreshToken, setRefreshToken } from './tokenStore';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface ApiResponse<T = any> {
  status: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// In-flight request deduplication + short TTL cache for GET requests
const inflightRequests = new Map<string, Promise<ApiResponse>>();
const responseCache = new Map<string, { data: ApiResponse; timestamp: number }>();
const CACHE_TTL = 5000; // 5 seconds

/**
 * Subscription gate: the API answers 403 with one of these codes when the
 * caller's institution has no usable subscription. Handled centrally so every
 * page reacts the same way instead of each one rendering a generic failure.
 */
const SUBSCRIPTION_BLOCKED_CODES = [
  'SUBSCRIPTION_EXPIRED',
  'SUBSCRIPTION_CANCELLED',
  'SUBSCRIPTION_PENDING',
];

/** Where an institution admin goes to renew. */
const RENEW_PATH = '/admin/subscription';

type SubscriptionBlockListener = (block: { code: string; message: string }) => void;
let subscriptionBlockListener: SubscriptionBlockListener | null = null;

/** Registered once by the app shell so it can show the renewal notice. */
export const onSubscriptionBlocked = (listener: SubscriptionBlockListener | null) => {
  subscriptionBlockListener = listener;
};

// Many requests can fail at once on a dashboard; announce the block only once.
let subscriptionBlockAnnouncedAt = 0;

const handleSubscriptionBlocked = (code: string, message: string) => {
  const now = Date.now();
  if (now - subscriptionBlockAnnouncedAt < 3000) return;
  subscriptionBlockAnnouncedAt = now;

  subscriptionBlockListener?.({ code, message });

  // Nudge the user to the renewal page unless they are already on it.
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith(RENEW_PATH)) {
    window.location.assign(RENEW_PATH);
  }
};

// ── Refresh: single shared in-flight promise ───────────────────────
// Every caller that needs a refresh — the proactive timer, a pre-request
// expiry check, a reactive 401, or the app-load silent refresh in
// AuthGate — goes through this same promise. If N requests all get a 401
// at once, only the FIRST one actually calls POST /auth/refresh; the other
// N-1 just await this promise and retry with whatever token it produces.
// This is what stops parallel requests from each rotating (and thereby
// invalidating) each other's refresh token.

/**
 * Outcome of a refresh attempt.
 *
 * The distinction between `rejected` and `transient` is the whole point:
 * ONLY `rejected` means the server actually told us the refresh cookie is no
 * good. Anything else — a thrown fetch, a 5xx, a timeout — means we never got
 * an answer, so we must NOT conclude the session is dead.
 *
 * Conflating the two is what caused users to be logged out whenever the dev
 * server restarted mid-refresh. Normal API requests in this file already treat
 * 5xx as transient ("don't log the user out"); this applies the same reasoning
 * to the refresh call itself. Do not collapse this back to a boolean.
 */
export type RefreshResult = 'success' | 'rejected' | 'transient';

let refreshPromise: Promise<RefreshResult> | null = null;

/** Backoff between retries of a transient failure, in ms. Length = retry count. */
const TRANSIENT_RETRY_DELAYS_MS = [500, 1500];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * One refresh attempt. Sends the HttpOnly refresh cookie automatically — no
 * body, no Authorization header.
 */
const attemptTokenRefresh = async (reason: string): Promise<RefreshResult> => {
  const startedAt = new Date().toISOString();
  try {
    const storedRefreshToken = getRefreshToken();
    const response = await fetch(`${API.BASE_URL}${API.REFRESH}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: storedRefreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      setAccessToken(data.token);
      if (data.refreshToken) setRefreshToken(data.refreshToken);
      console.log(`[Auth] Refresh SUCCEEDED — reason="${reason}" at=${startedAt}`);
      return 'success';
    }

    if (response.status === 401 || response.status === 403) {
      console.warn(`[Auth] Refresh FAILED — reason="${reason}" status=${response.status} at=${startedAt}`);
      setAccessToken(null);
      setRefreshToken(null);
      return 'rejected';
    }

    console.warn(`[Auth] Refresh FAILED — non-auth status=${response.status} reason="${reason}" at=${startedAt}`);
    return 'transient';
  } catch (err: any) {
    console.warn(`[Auth] Refresh ERRORED — reason="${reason}" at=${startedAt}:`, err?.message || err);
    return 'transient';
  }
};

/**
 * Attempt a refresh, retrying transient failures with a short backoff.
 * Gives up after TRANSIENT_RETRY_DELAYS_MS.length retries and returns
 * 'transient' — which callers must treat as "keep the session".
 */
const refreshWithRetry = async (reason: string): Promise<RefreshResult> => {
  const totalAttempts = TRANSIENT_RETRY_DELAYS_MS.length + 1;

  for (let attempt = 1; attempt <= totalAttempts; attempt += 1) {
    const result = await attemptTokenRefresh(reason);
    if (result !== 'transient') return result;

    if (attempt < totalAttempts) {
      const delay = TRANSIENT_RETRY_DELAYS_MS[attempt - 1];
      console.warn(
        `[Auth] Refresh transient failure — retrying (attempt ${attempt + 1}/${totalAttempts}) in ${delay}ms`,
      );
      await sleep(delay);
    }
  }

  console.warn(
    `[Auth] Refresh transient failure — giving up after ${TRANSIENT_RETRY_DELAYS_MS.length} retries, keeping session`,
  );
  return 'transient';
};

/** Coalesces concurrent refresh attempts into the single shared promise described above. */
const getSharedRefresh = (reason: string): Promise<RefreshResult> => {
  if (!refreshPromise) {
    refreshPromise = refreshWithRetry(reason).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

/**
 * Public entry point used by AuthGate on app load / hard refresh, and by
 * anything else that wants to force a refresh outside the request path.
 * Goes through the same shared promise as the reactive/proactive paths.
 */
export const refreshSession = (reason: string): Promise<RefreshResult> => getSharedRefresh(reason);

// ── Proactive token refresh ─────────────────────────────────────────────
// Refresh ~1-2 minutes before the access token's known expiry so normal use
// rarely hits a reactive 401-triggered refresh at all.
const PROACTIVE_REFRESH_BUFFER_MS = 120_000;

let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

const scheduleProactiveRefresh = () => {
  if (proactiveRefreshTimer) clearTimeout(proactiveRefreshTimer);

  const token = getAccessToken();
  if (!token) return;
  const exp = decodeTokenExp(token);
  if (!exp) return;

  // Floor at 5s so we never schedule something in the past or fire in a tight loop.
  const msUntilRefresh = Math.max(exp * 1000 - Date.now() - PROACTIVE_REFRESH_BUFFER_MS, 5_000);

  proactiveRefreshTimer = setTimeout(async () => {
    const result = await getSharedRefresh('proactive-timer');
    if (result !== 'success') {
      // Neither outcome logs out here. A 'rejected' already cleared the token,
      // so the next API call's 401 path decides; a 'transient' keeps the
      // current token and simply tries again on the next request.
      console.warn(
        `[Auth] Proactive refresh did not succeed (${result}) — will retry reactively on next API call`,
      );
    }
    // On success, attemptTokenRefresh() already rescheduled via scheduleProactiveRefresh().
  }, msUntilRefresh);
};

const isTokenExpiringSoon = (): boolean => {
  const token = getAccessToken();
  if (!token) return false; // no token = not logged in, nothing to refresh
  const exp = decodeTokenExp(token);
  if (!exp) return false;
  return exp * 1000 - Date.now() < PROACTIVE_REFRESH_BUFFER_MS;
};

// Reschedule (or clear) the proactive timer every time the access token
// changes, no matter who set it — login, DevLogin, a reactive/proactive
// refresh, or AuthGate's app-load refresh.
subscribeAccessToken((token) => {
  if (token) {
    scheduleProactiveRefresh();
  } else if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
});

// ── Centralized logout helper ───────────────────────────────────────────
// Ensures we only fire the redirect once even if many parallel requests
// all fail at the same time. The flag is never reset — once a session is
// dead the user must go through the login flow.
let logoutFired = false;

const performLogout = (reason: string) => {
  if (logoutFired) return;
  logoutFired = true;

  console.error(
    '[Auth] Session terminated — performLogout fired',
    `\n  reason: ${reason}`,
    '\n  now:', new Date().toISOString(),
    '\n  stack:', new Error().stack,
  );

  setAccessToken(null);
  setRefreshToken(null);
  Store.dispatch(logout());
  // Small delay so redux-persist can flush before the page navigates
  setTimeout(() => {
    window.location.href = '/';
  }, 150);
};

const apiRequest = async <T = any>(
  method: HttpMethod,
  endpoint: string,
  body?: any,
  skipAuth: boolean = false,
): Promise<ApiResponse<T>> => {
  try {
    const url = `${API.BASE_URL}${endpoint}`;
    const headers: Record<string, string> = {};

    if (!(body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    if (!skipAuth) {
      // If the token is about to expire, refresh proactively before sending
      // — avoids the 401 round-trip. Shares the same in-flight promise as
      // every other refresh path, so this never races a reactive refresh.
      if (isTokenExpiringSoon()) {
        await getSharedRefresh('pre-request-expiring-soon');
      }

      const token = getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const options: RequestInit = {
      method,
      headers,
      credentials: 'include', // carries the HttpOnly refresh cookie when the endpoint needs it
    };

    if (body && method !== 'GET') {
      options.body = body instanceof FormData ? body : JSON.stringify(body);
    }

    let response = await fetch(url, options);

    // On 401, try refreshing the token once. Only a failed REFRESH call
    // (not the original request's 401) triggers a logout — see below.
    if (response.status === 401 && !skipAuth) {
      console.warn(`[Auth] 401 received for ${method} ${endpoint} — attempting refresh`);

      const refreshResult = await getSharedRefresh(`reactive-401:${method} ${endpoint}`);

      if (refreshResult === 'success') {
        const newToken = getAccessToken();
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
        }
        options.headers = headers;
        response = await fetch(url, options);
      } else if (refreshResult === 'rejected') {
        // Only a real 401/403 from /auth/refresh ends the session.
        console.error(`[Auth] Refresh rejected after 401 on ${method} ${endpoint} — logging out`);
        performLogout(`refresh rejected after 401 on ${method} ${endpoint}`);
        return { status: false, message: 'Session expired. Please login again.' };
      } else {
        // Transient: the server never told us the session is invalid (it was
        // unreachable or returned 5xx), so we keep the session and surface a
        // retryable error. Do NOT conflate this with an auth rejection — doing
        // so is what logged users out on every dev-server restart.
        console.warn(
          `[Auth] Refresh transient after 401 on ${method} ${endpoint} — keeping session, request not retried`,
        );
        return {
          status: false,
          message: 'Could not reach the server. Please check your connection and try again.',
        };
      }
    }

    // 5xx errors are transient server issues — don't log the user out
    if (response.status >= 500) {
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      return {
        status: false,
        message: data.message || 'Server error — please try again',
        error: data.error,
        data,
      };
    }

    const text = await response.text();
    const data = text ? JSON.parse(text) : {};

    if (!response.ok) {
      if (response.status === 403 && SUBSCRIPTION_BLOCKED_CODES.includes(data.code)) {
        handleSubscriptionBlocked(
          data.code,
          data.message || 'Your subscription has expired. Please renew your plan to continue.',
        );
      }
      return {
        status: false,
        message: data.message || 'Something went wrong',
        error: data.error,
        data,
      };
    }

    return { status: true, data };
  } catch (error: any) {
    return {
      status: false,
      message: error.message || 'Network error',
    };
  }
};

export const GET = <T = any>(endpoint: string, skipAuth?: boolean): Promise<ApiResponse<T>> => {
  const cacheKey = `${endpoint}:${skipAuth ? '1' : '0'}`;

  // Return cached response if still fresh
  const cached = responseCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return Promise.resolve(cached.data as ApiResponse<T>);
  }

  // Deduplicate in-flight requests for the same endpoint
  const inflight = inflightRequests.get(cacheKey);
  if (inflight) return inflight as Promise<ApiResponse<T>>;

  const request = apiRequest<T>('GET', endpoint, undefined, skipAuth).then(result => {
    inflightRequests.delete(cacheKey);
    if (result.status) {
      responseCache.set(cacheKey, { data: result, timestamp: Date.now() });
    }
    return result;
  });

  inflightRequests.set(cacheKey, request as Promise<ApiResponse>);
  return request;
};

export const invalidateCache = (endpointPrefix?: string) => {
  if (!endpointPrefix) {
    responseCache.clear();
    return;
  }
  Array.from(responseCache.keys()).forEach(key => {
    if (key.startsWith(endpointPrefix)) responseCache.delete(key);
  });
};

const mutateAndInvalidate = <T = any>(
  method: HttpMethod,
  endpoint: string,
  body?: any,
  skipAuth?: boolean,
): Promise<ApiResponse<T>> =>
  apiRequest<T>(method, endpoint, body, skipAuth).then(result => {
    if (result.status) responseCache.clear();
    return result;
  });

export const POST = <T = any>(endpoint: string, body?: any, skipAuth?: boolean) =>
  mutateAndInvalidate<T>('POST', endpoint, body, skipAuth);

export const PUT = <T = any>(endpoint: string, body?: any) =>
  mutateAndInvalidate<T>('PUT', endpoint, body);

export const PATCH = <T = any>(endpoint: string, body?: any) =>
  mutateAndInvalidate<T>('PATCH', endpoint, body);

export const DELETE = <T = any>(endpoint: string, body?: any) =>
  mutateAndInvalidate<T>('DELETE', endpoint, body);

export const UPLOAD = <T = any>(endpoint: string, body: FormData, skipAuth?: boolean) =>
  mutateAndInvalidate<T>('POST', endpoint, body, skipAuth);

export default apiRequest;
